// Golden Wilds on the page (shared/golden.ts): a golden copy of the hunt's wild that stands on the field for about ten
// seconds (or floats over any other page) and pays coins and XP when clicked. The server says when one shows; a click
// sends goldenClaim and the reward comes back as a hunt event, shown here as a toast. The corner window shows the same
// one (client/corner/hud.ts, through onGoldenChange).
//
// Also the two sounds in Settings: a chime when a Golden Wild shows (on unless switched off) and one on a catch
// (off unless chosen: grade A and up, or every catch kept).
import type { ClientMsg, GameEvent } from '../shared/protocol.ts';
import { GOLDEN, type GoldenDTO } from '../shared/golden.ts';
import { SPECIES } from '../shared/species.ts';
import { gradeFloor } from '../shared/grade.ts';
import { ratingOf } from '../shared/rating.ts';
import { S } from './state.ts';
import { creatureIcon } from './assets.ts';
import { esc, full } from './fmt.ts';
import { toast } from './dialog.ts';
import { batterySaver } from './field.ts';
import { chime } from './corner/chime.ts';

/** The Golden Wild on show: until is on this page's clock (Date.now); ms is its whole time on the field. */
export interface GoldenShown { id: number; species: string; until: number; ms: number; x: number; y: number }

let cur: GoldenShown | null = null;
let send: (m: ClientMsg) => void = () => {};
let el: HTMLButtonElement | null = null;
let timer = 0;
const listeners = new Set<() => void>();

export const goldenName = (species: string) => `Golden ${SPECIES[species]?.name ?? 'Wild'}`;

/** The Golden Wild on the field now, or null. */
export function goldenNow(): GoldenShown | null { return cur && Date.now() < cur.until ? cur : null; }

/** Called whenever one shows or goes (the corner window redraws its own). */
export function onGoldenChange(fn: () => void) { listeners.add(fn); return () => listeners.delete(fn); }

export function bindGolden(fn: (m: ClientMsg) => void) { send = fn; }

function changed() {
  drawGolden();
  for (const fn of listeners) { try { fn(); } catch { /* a listener's own problem */ } }
}

/** The server's word: a Golden Wild showed (with the time it has left), or the one on show is gone. */
export function onGolden(g: GoldenDTO | null) {
  const was = goldenNow()?.id;
  window.clearTimeout(timer);
  cur = g && g.left > 0 ? { id: g.id, species: g.species, until: Date.now() + g.left, ms: g.ms || GOLDEN.windowMs, x: g.x, y: g.y } : null;
  if (cur) timer = window.setTimeout(() => { cur = null; changed(); }, cur.until - Date.now() + 50);
  if (cur && cur.id !== was && S.trainer?.settings.goldenSound !== false) chime(window, 'golden');
  changed();
}

/** A click on it: gone at once; the server checks the time and the reward comes back as a toast. */
export function claimGolden() {
  const g = goldenNow();
  if (!g) return;
  send({ t: 'goldenClaim', id: g.id });
  window.clearTimeout(timer);
  cur = null;
  changed();
}

/** Places it: on the field while the hunt shows, else floating over the page (another page, Kinhall, battery saver). */
export function drawGolden() {
  const g = goldenNow();
  if (!g) { el?.remove(); el = null; return; }
  if (!el) {
    el = document.createElement('button');
    el.type = 'button';
    el.className = 'golden';
    el.addEventListener('click', (e) => { e.stopPropagation(); claimGolden(); });
  }
  if (el.dataset.id !== String(g.id)) {
    el.dataset.id = String(g.id);
    const name = goldenName(g.species);
    el.setAttribute('aria-label', `${name}: click for coins and XP`);
    el.title = `${name} · click for coins and XP`;
    el.innerHTML = `<span class="golden-pic">${creatureIcon(g.species, false, '')}</span><span class="golden-name">${esc(name)}</span><span class="golden-time"><i></i></span>`;
  }
  const canvas = document.getElementById('field') as HTMLCanvasElement | null;
  const stage = canvas?.parentElement ?? null;
  const onField = !!(canvas && stage && !S.tab && !batterySaver() && canvas.offsetWidth > 0 && canvas.offsetHeight > 0);
  const host = onField ? stage! : document.body;
  if (el.parentElement !== host) {
    host.append(el);
    // The bar runs from where the time is now: a move between the field and the page restarts its animation.
    const bar = el.querySelector('.golden-time > i') as HTMLElement;
    bar.style.animationDuration = `${g.ms}ms`;
    bar.style.animationDelay = `${-Math.max(0, g.ms - (g.until - Date.now()))}ms`;
  }
  el.classList.toggle('float', !onField);
  if (onField) {
    el.style.left = `${canvas!.offsetLeft + g.x * canvas!.offsetWidth}px`;
    el.style.top = `${canvas!.offsetTop + g.y * canvas!.offsetHeight}px`;
  } else el.style.left = el.style.top = '';
}
window.addEventListener('resize', () => drawGolden());

/** The reward toast, and the catch chime the player chose. corner: the corner window chimes for Auras itself. */
export function goldenEvents(evs: GameEvent[], corner: { auraChime: boolean }) {
  const pick = S.trainer?.settings.catchSound ?? 'off';
  let ring: 'catch' | 'aura-caught' | null = null;
  for (const e of evs) {
    if (e.k === 'golden') toast(`${goldenName(e.species)}: +${full(e.coins)} coins, +${full(e.xp)} XP`, 'gold');
    else if (e.k === 'catch' && !e.sold && pick !== 'off') {
      const c = e.creature;
      // An Aura rings its own bell, whatever its grade.
      if (c.rolls.aura) { if (!corner.auraChime) ring = 'aura-caught'; } else if (pick === 'all' || ratingOf(c.rolls, c.species) >= gradeFloor('A')) ring ??= 'catch';
    }
  }
  if (ring) chime(window, ring);
}
